/** Pet Taxi route parsing, geocoding fallbacks and map embed URLs. */

import { geocodeAddress } from './petTaxiPricing';

export const COUNTRY_ROUTE_FALLBACK = {
  DE: {
    fromStreet: 'Pariser Platz', fromCity: 'Berlin',
    toStreet: 'Marienplatz', toCity: 'München',
    fromLat: 52.5163, fromLon: 13.3777,
    toLat: 48.1374, toLon: 11.5755,
  },
  PL: {
    fromStreet: 'Marszałkowska 1', fromCity: 'Warszawa',
    toStreet: 'Floriańska 15', toCity: 'Kraków',
    fromLat: 52.2297, fromLon: 21.0122,
    toLat: 50.0647, toLon: 19.945,
  },
  RO: {
    fromStreet: 'Calea Victoriei 45', fromCity: 'București',
    toStreet: 'Strada Memorandumului 28', toCity: 'Cluj-Napoca',
    fromLat: 44.4268, fromLon: 26.1025,
    toLat: 46.7712, toLon: 23.6236,
  },
  PT: {
    fromStreet: 'Avenida da Liberdade 100', fromCity: 'Lisboa',
    toStreet: 'Rua de Santa Catarina 200', toCity: 'Porto',
    fromLat: 38.7223, fromLon: -9.1393,
    toLat: 41.1579, toLon: -8.6291,
  },
  FI: {
    fromStreet: 'Mannerheimintie 1', fromCity: 'Helsinki',
    toStreet: 'Hämeenkatu 10', toCity: 'Tampere',
    fromLat: 60.1699, fromLon: 24.9384,
    toLat: 61.4981, toLon: 23.761,
  },
  CH: {
    fromStreet: 'Bahnhofstrasse 1', fromCity: 'Zürich',
    toStreet: 'Rue du Rhône 10', toCity: 'Genève',
    fromLat: 47.3769, fromLon: 8.5417,
    toLat: 46.2044, toLon: 6.1432,
  },
  GB: {
    fromStreet: 'Oxford Street 100', fromCity: 'London',
    toStreet: 'Deansgate 50', toCity: 'Manchester',
    fromLat: 51.5154, fromLon: -0.1419,
    toLat: 53.4808, toLon: -2.2426,
  },
  IT: {
    fromStreet: 'Via del Corso 100', fromCity: 'Roma',
    toStreet: 'Via Montenapoleone 8', toCity: 'Milano',
    fromLat: 41.902, fromLon: 12.496,
    toLat: 45.464, toLon: 9.190,
  },
  FR: {
    fromStreet: '10 Avenue des Champs-Élysées', fromCity: 'Paris',
    toStreet: '20 Rue de la République', toCity: 'Lyon',
    fromLat: 48.856, fromLon: 2.352,
    toLat: 45.764, toLon: 4.836,
  },
  NO: {
    fromStreet: 'Karl Johans gate 1', fromCity: 'Oslo',
    toStreet: 'Torgallmenningen 2', toCity: 'Bergen',
    fromLat: 59.913, fromLon: 10.752,
    toLat: 60.391, toLon: 5.324,
  },
  SE: {
    fromStreet: 'Drottninggatan 1', fromCity: 'Stockholm',
    toStreet: 'Avenyn 10', toCity: 'Göteborg',
    fromLat: 59.329, fromLon: 18.069,
    toLat: 57.708, toLon: 11.974,
  },
  DK: {
    fromStreet: 'Strøget 1', fromCity: 'København',
    toStreet: 'Store Torv 1', toCity: 'Aarhus',
    fromLat: 55.676, fromLon: 12.568,
    toLat: 56.162, toLon: 10.203,
  },
  AT: {
    fromStreet: 'Ringstraße 1', fromCity: 'Wien',
    toStreet: 'Herrengasse 10', toCity: 'Graz',
    fromLat: 48.208, fromLon: 16.373,
    toLat: 47.070, toLon: 15.439,
  },
  HU: {
    fromStreet: 'Andrássy út 1', fromCity: 'Budapest',
    toStreet: 'Piac utca 1', toCity: 'Debrecen',
    fromLat: 47.497, fromLon: 19.040,
    toLat: 47.531, toLon: 21.625,
  },
};

export function formatRouteAddress(street, city) {
  return [street, city].filter((p) => (p || '').trim()).join(', ');
}

export function splitStreetCity(text) {
  const raw = (text || '').trim();
  if (!raw) return { street: '', city: '' };
  if (raw.includes(',')) {
    const idx = raw.lastIndexOf(',');
    return { street: raw.slice(0, idx).trim(), city: raw.slice(idx + 1).trim() };
  }
  return { street: '', city: raw };
}

export function parseDeliveryAddressRoute(addr) {
  if (!addr) return null;
  const text = String(addr).trim();
  const sep = text.includes('→') ? '→' : (text.includes('->') ? '->' : '');
  if (!sep) return null;
  const parts = text.split(sep);
  if (parts.length < 2) return null;
  const from = splitStreetCity(parts[0]);
  const to = splitStreetCity(parts[1]);
  return {
    fromStreet: from.street,
    fromCity: from.city,
    toStreet: to.street,
    toCity: to.city,
  };
}

function hasRouteEndpoint(street, city) {
  return Boolean((street || '').trim() || (city || '').trim());
}

export function resolvePetTaxiRoute(page) {
  if (!page) return null;
  const meta = page.meta || {};
  let fromStreet = (meta.from_street || '').trim();
  let fromCity = (meta.from_city || '').trim();
  let toStreet = (meta.to_street || '').trim();
  let toCity = (meta.to_city || '').trim();

  if ((!fromCity || !toCity) && page.delivery_address) {
    const parsed = parseDeliveryAddressRoute(page.delivery_address);
    if (parsed) {
      fromStreet = fromStreet || parsed.fromStreet;
      fromCity = fromCity || parsed.fromCity;
      toStreet = toStreet || parsed.toStreet;
      toCity = toCity || parsed.toCity;
    }
  }

  if (fromStreet && !fromCity && fromStreet.includes(',')) {
    const p = splitStreetCity(fromStreet);
    fromStreet = p.street;
    fromCity = p.city;
  }
  if (toStreet && !toCity && toStreet.includes(',')) {
    const p = splitStreetCity(toStreet);
    toStreet = p.street;
    toCity = p.city;
  }

  if (!hasRouteEndpoint(fromStreet, fromCity) || !hasRouteEndpoint(toStreet, toCity)) {
    return null;
  }

  return {
    fromStreet,
    fromCity,
    toStreet,
    toCity,
    fromLat: meta.from_lat,
    fromLon: meta.from_lon,
    toLat: meta.to_lat,
    toLon: meta.to_lon,
    distanceKm: meta.distance_km,
    driveTime: meta.drive_time,
    isFallback: Boolean(meta.route_fallback),
  };
}

export function ensureRouteForMap(route, countryCode = 'DE') {
  const cc = (countryCode || 'DE').toUpperCase();
  const resolved = route || null;
  if (
    resolved &&
    hasRouteEndpoint(resolved.fromStreet, resolved.fromCity) &&
    hasRouteEndpoint(resolved.toStreet, resolved.toCity)
  ) {
    return resolved;
  }
  const fb = COUNTRY_ROUTE_FALLBACK[cc] || COUNTRY_ROUTE_FALLBACK.DE;
  return {
    fromStreet: fb.fromStreet,
    fromCity: fb.fromCity,
    toStreet: fb.toStreet,
    toCity: fb.toCity,
    fromLat: fb.fromLat,
    fromLon: fb.fromLon,
    toLat: fb.toLat,
    toLon: fb.toLon,
    distanceKm: resolved?.distanceKm,
    driveTime: resolved?.driveTime,
    isFallback: true,
  };
}

export function interpolateRoutePoint(fromLat, fromLon, toLat, toLon, progress = 0.5) {
  if ([fromLat, fromLon, toLat, toLon].some((v) => v == null || Number.isNaN(Number(v)))) {
    return null;
  }
  const t = Math.min(1, Math.max(0, Number(progress)));
  return {
    lat: Number(fromLat) + (Number(toLat) - Number(fromLat)) * t,
    lon: Number(fromLon) + (Number(toLon) - Number(fromLon)) * t,
  };
}

export function buildOsmEmbedSrc(fromLat, fromLon, toLat, toLon, routeProgress = 0) {
  const minLat = Math.min(fromLat, toLat) - 0.12;
  const maxLat = Math.max(fromLat, toLat) + 0.12;
  const minLon = Math.min(fromLon, toLon) - 0.18;
  const maxLon = Math.max(fromLon, toLon) + 0.18;
  const bbox = `${minLon},${minLat},${maxLon},${maxLat}`;
  let url =
    'https://www.openstreetmap.org/export/embed.html' +
    `?bbox=${encodeURIComponent(bbox)}&layer=mapnik` +
    `&marker=${fromLat}%2C${fromLon}&marker=${toLat}%2C${toLon}`;
  if (routeProgress > 0 && routeProgress < 1) {
    const mid = interpolateRoutePoint(fromLat, fromLon, toLat, toLon, routeProgress);
    if (mid) url += `&marker=${mid.lat}%2C${mid.lon}`;
  }
  return url;
}

export function buildGoogleMapsEmbedSrc(fromStreet, fromCity, toStreet, toCity, lang = 'de') {
  const origin = formatRouteAddress(fromStreet, fromCity);
  const destination = formatRouteAddress(toStreet, toCity);
  if (!origin || !destination) return '';
  return buildGoogleMapsDirectionsEmbedSrc(origin, destination, lang);
}

export function buildGoogleMapsDirectionsEmbedSrc(origin, destination, lang = 'de') {
  if (!origin || !destination) return '';
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (apiKey) {
    return (
      `https://www.google.com/maps/embed/v1/directions?key=${apiKey}` +
      `&origin=${encodeURIComponent(origin)}` +
      `&destination=${encodeURIComponent(destination)}` +
      `&mode=driving&language=${lang}`
    );
  }
  return (
    `https://maps.google.com/maps?saddr=${encodeURIComponent(origin)}` +
    `&daddr=${encodeURIComponent(destination)}&hl=${lang}&output=embed`
  );
}

export function buildGoogleMapsCoordEmbedSrc(fromLat, fromLon, toLat, toLon, lang = 'de') {
  if ([fromLat, fromLon, toLat, toLon].some((v) => v == null || Number.isNaN(Number(v)))) return '';
  const origin = `${fromLat},${fromLon}`;
  const destination = `${toLat},${toLon}`;
  return buildGoogleMapsDirectionsEmbedSrc(origin, destination, lang);
}

/** OSRM demo map — shows driving route line between two points (no API key). */
export function buildOsrmMapEmbedSrc(fromLat, fromLon, toLat, toLon, routeProgress = 0) {
  if ([fromLat, fromLon, toLat, toLon].some((v) => v == null || Number.isNaN(Number(v)))) return '';
  const centerLat = (Number(fromLat) + Number(toLat)) / 2;
  const centerLon = (Number(fromLon) + Number(toLon)) / 2;
  const span = Math.max(Math.abs(fromLat - toLat), Math.abs(fromLon - toLon), 0.4);
  const z = span > 10 ? 5 : span > 5 ? 6 : span > 2 ? 7 : span > 0.8 ? 8 : 9;
  let url =
    `https://map.project-osrm.org/?z=${z}` +
    `&center=${centerLat},${centerLon}` +
    `&loc=${fromLat},${fromLon}`;
  if (routeProgress > 0 && routeProgress < 1) {
    const mid = interpolateRoutePoint(fromLat, fromLon, toLat, toLon, routeProgress);
    if (mid) url += `&loc=${mid.lat},${mid.lon}`;
  }
  url += `&loc=${toLat},${toLon}`;
  return url;
}

async function geocodeFlexible(street, city, countryCode) {
  const s = (street || '').trim();
  const c = (city || '').trim();
  if (!s && !c) return null;
  let point = await geocodeAddress(s, c, countryCode);
  if (!point && c) point = await geocodeAddress('', c, countryCode);
  if (!point && s) point = await geocodeAddress(s, '', countryCode);
  return point;
}

function buildRouteMapEmbedSrc({
  fromStreet,
  fromCity,
  toStreet,
  toCity,
  fromLat,
  fromLon,
  toLat,
  toLon,
  lang,
  routeProgress = 0,
}) {
  const googleByAddress = buildGoogleMapsEmbedSrc(fromStreet, fromCity, toStreet, toCity, lang);
  if (googleByAddress) return googleByAddress;

  if (fromLat && fromLon && toLat && toLon) {
    return (
      buildGoogleMapsCoordEmbedSrc(fromLat, fromLon, toLat, toLon, lang) ||
      buildOsrmMapEmbedSrc(fromLat, fromLon, toLat, toLon, routeProgress) ||
      buildOsmEmbedSrc(fromLat, fromLon, toLat, toLon, routeProgress)
    );
  }

  return '';
}

export async function resolveMapEmbedSrc(route, { lang = 'de', countryCode = 'DE', routeProgress = 0 } = {}) {
  const r = ensureRouteForMap(route, countryCode);
  const progress = route?.routeProgress ?? routeProgress ?? 0;
  const { fromStreet, fromCity, toStreet, toCity, fromLat, fromLon, toLat, toLon } = r;

  const direct = buildRouteMapEmbedSrc({
    fromStreet,
    fromCity,
    toStreet,
    toCity,
    fromLat,
    fromLon,
    toLat,
    toLon,
    lang,
    routeProgress: progress,
  });
  if (direct) return direct;

  const from = await geocodeFlexible(fromStreet, fromCity, countryCode);
  const to = await geocodeFlexible(toStreet, toCity, countryCode);
  if (from && to) {
    return (
      buildGoogleMapsCoordEmbedSrc(from.lat, from.lon, to.lat, to.lon, lang) ||
      buildOsrmMapEmbedSrc(from.lat, from.lon, to.lat, to.lon, progress) ||
      buildOsmEmbedSrc(from.lat, from.lon, to.lat, to.lon, progress)
    );
  }

  const fb = COUNTRY_ROUTE_FALLBACK[countryCode?.toUpperCase()] || COUNTRY_ROUTE_FALLBACK.DE;
  return (
    buildGoogleMapsEmbedSrc(fb.fromStreet, fb.fromCity, fb.toStreet, fb.toCity, lang) ||
    buildOsrmMapEmbedSrc(fb.fromLat, fb.fromLon, fb.toLat, fb.toLon, progress) ||
    buildOsmEmbedSrc(fb.fromLat, fb.fromLon, fb.toLat, fb.toLon, progress)
  );
}
